// Layer 2 (Engine): session-local FMEA learning from an approved case.
// Seed rows remain unchanged. D19: an action approval raises a draft proposal;
// only the Senior Metallurgist seat's approval adds the override and diff record.
// K13c: the proposal also carries what the case taught the row — a learned cause
// and early warning, drawn as additions and live only after that second gate.

import type { FailureMode } from '@/versions/v1/schema/case';

export type FmeaLearningRowState = {
  failureModeId: string;
  occurrenceCount: number;
  s: number;
  o: number;
  d: number;
  rpn: number;
  lastSeen: string | null;
  earlyWarning: string;
  evidenceCount: number;
  /** K13c: the cause this case taught the row, drawn as a "+" line under the cause. */
  learnedCause?: string;
  /** K13c: the early warning this case taught the row, drawn as a "+" line under the warning. */
  learnedEarlyWarning?: string;
  updatedAt?: string;
  updatedBy?: string;
};

/** A live FMEA change: the Senior Metallurgist seat approved the proposal (D19). */
export type FmeaLearningApproval = {
  caseId: string;
  caseCode: string;
  causeId: string;
  failureModeId: string;
  /** When and by which seat the FMEA change went live. */
  updatedAt: string;
  updatedBy: string;
  /** The action approval that raised the proposal. */
  proposedAt: string;
  proposedBy: string;
  reason?: string;
  before: FmeaLearningRowState;
  after: FmeaLearningRowState;
};

/**
 * D19: approving a case's action raises its FMEA change as a draft proposal.
 * The row stays as it was until the Senior Metallurgist seat approves it.
 */
export type FmeaLearningProposal = {
  caseId: string;
  caseCode: string;
  causeId: string;
  failureModeId: string;
  failureMode: FailureMode;
  excursionDate: string;
  earlyWarning?: string;
  /** K13c: additive learned lines; they never replace the row's cause or warning. */
  learnedCause?: string;
  learnedEarlyWarning?: string;
  proposedAt: string;
  proposedBy: string;
  reason?: string;
};

export type FmeaLearningSessionState = {
  /** Live row overrides; only an approved proposal writes here. */
  rows: ReadonlyMap<string, FmeaLearningRowState>;
  /** Live FMEA changes by case id. */
  approvals: ReadonlyMap<string, FmeaLearningApproval>;
  /** Draft FMEA changes by case id, awaiting the Senior Metallurgist seat. */
  proposals: ReadonlyMap<string, FmeaLearningProposal>;
};

/** A case's action approval: what the proposal records about the case and its cause. */
export type FmeaLearningProposalInput = {
  caseId: string;
  caseCode: string;
  causeId: string;
  failureMode: FailureMode;
  excursionDate: string;
  proposedAt: string;
  proposedBy: string;
  reason?: string;
  earlyWarning?: string;
  /** K13c: what the case's diagnosis teaches the row, kept as additions. */
  learnedCause?: string;
  learnedEarlyWarning?: string;
};

/** The second gate: the seat that approves the proposal and when. */
export type FmeaProposalApprovalInput = {
  caseId: string;
  approvedAt: string;
  approvedBy: string;
};

export const INITIAL_FMEA_LEARNING_STATE: FmeaLearningSessionState = {
  rows: new Map(),
  approvals: new Map(),
  proposals: new Map(),
};

/** Analytics cause ids are linked to the workbook-shaped FMEA row they learn. */
const FAILURE_MODE_BY_CAUSE_ID: Readonly<Record<string, string>> = {
  'fmea-zn-lime-underfeed': 'fm-zn-ph',
  'fm-trunnion': 'fm-trunnion',
};

export function failureModeIdForCauseId(causeId: string): string | undefined {
  return FAILURE_MODE_BY_CAUSE_ID[causeId];
}

/**
 * The zinc FMEA workbook row carries S/O/D and RPN, but no occurrence tally,
 * evidence count, or last-seen stamp. These display baselines are synthetic
 * seed metadata aligned with the existing O=7 row; they never change the seed.
 */
const SYNTHETIC_BASELINE: Readonly<Record<string, Pick<FmeaLearningRowState,
  'occurrenceCount' | 'lastSeen' | 'evidenceCount'>>> = {
  'fm-zn-ph': {
    occurrenceCount: 7, // [syn] initial event tally, giving the seeded O rating of 7
    lastSeen: '2026-05-14', // [syn] no last-seen date is carried by the workbook row
    evidenceCount: 3, // [syn] previous linked cases absent from the workbook row
  },
};

/** Read the session override, or the workbook row plus any synthetic baseline. */
export function fmeaLearningRow(
  state: FmeaLearningSessionState,
  failureMode: FailureMode,
): FmeaLearningRowState {
  const override = state.rows.get(failureMode.id);
  if (override) return override;
  const baseline = SYNTHETIC_BASELINE[failureMode.id];
  return {
    failureModeId: failureMode.id,
    occurrenceCount: baseline?.occurrenceCount ?? failureMode.o,
    s: failureMode.s,
    o: failureMode.o,
    d: failureMode.d,
    rpn: failureMode.rpn,
    lastSeen: baseline?.lastSeen ?? null,
    earlyWarning: failureMode.earlyWarning,
    evidenceCount: baseline?.evidenceCount ?? 0,
  };
}

/**
 * The row one case would produce on top of the current live row. The mock
 * uses an explicit synthetic count scale: O is the integer occurrence tally,
 * capped at 10. RPN is S×O×D. Pure; nothing is recorded.
 *
 * K13c: `learnedCause` / `learnedEarlyWarning` ride along as additions. They
 * never replace the row's cause or warning text, so the column's own copy and
 * the case's lesson both read.
 */
export function fmeaChangeFor(
  state: FmeaLearningSessionState,
  input: Pick<FmeaLearningProposalInput, 'failureMode' | 'excursionDate' | 'earlyWarning' | 'learnedCause' | 'learnedEarlyWarning'>,
): { before: FmeaLearningRowState; after: FmeaLearningRowState } {
  const before = fmeaLearningRow(state, input.failureMode);
  const occurrenceCount = before.occurrenceCount + 1;
  const o = Math.min(10, occurrenceCount);
  const earlyWarning = input.earlyWarning?.trim() || before.earlyWarning;
  const learnedCause = input.learnedCause?.trim() || before.learnedCause;
  const learnedEarlyWarning = input.learnedEarlyWarning?.trim() || before.learnedEarlyWarning;
  return {
    before,
    after: {
      failureModeId: input.failureMode.id,
      occurrenceCount,
      s: input.failureMode.s,
      o,
      d: input.failureMode.d,
      rpn: input.failureMode.s * o * input.failureMode.d,
      lastSeen: input.excursionDate.slice(0, 10),
      earlyWarning,
      evidenceCount: before.evidenceCount + 1,
      ...(learnedCause ? { learnedCause } : {}),
      ...(learnedEarlyWarning ? { learnedEarlyWarning } : {}),
    },
  };
}

/**
 * The action gate (D19): raise the case's FMEA change as a draft proposal.
 * The live row does not move. A case already proposed or live is ignored, so
 * one case contributes at most once.
 */
export function proposeFmeaChange(
  state: FmeaLearningSessionState,
  input: FmeaLearningProposalInput,
): FmeaLearningSessionState {
  if (state.approvals.has(input.caseId) || state.proposals.has(input.caseId)) return state;
  const proposals = new Map(state.proposals);
  proposals.set(input.caseId, {
    caseId: input.caseId,
    caseCode: input.caseCode,
    causeId: input.causeId,
    failureModeId: input.failureMode.id,
    failureMode: input.failureMode,
    excursionDate: input.excursionDate,
    earlyWarning: input.earlyWarning,
    learnedCause: input.learnedCause,
    learnedEarlyWarning: input.learnedEarlyWarning,
    proposedAt: input.proposedAt,
    proposedBy: input.proposedBy,
    reason: input.reason,
  });
  return { ...state, proposals };
}

/**
 * The FMEA gate (D19): the Senior Metallurgist seat approves a proposal, and
 * only then does the row take the change. The change is computed against the
 * live row at approval time. An unknown or already-live case is ignored.
 */
export function approveFmeaProposal(
  state: FmeaLearningSessionState,
  input: FmeaProposalApprovalInput,
): FmeaLearningSessionState {
  const proposal = state.proposals.get(input.caseId);
  if (!proposal || state.approvals.has(input.caseId)) return state;
  const { before, after: change } = fmeaChangeFor(state, proposal);
  const after: FmeaLearningRowState = {
    ...change,
    updatedAt: input.approvedAt,
    updatedBy: input.approvedBy,
  };
  const approval: FmeaLearningApproval = {
    caseId: proposal.caseId,
    caseCode: proposal.caseCode,
    causeId: proposal.causeId,
    failureModeId: proposal.failureModeId,
    updatedAt: input.approvedAt,
    updatedBy: input.approvedBy,
    proposedAt: proposal.proposedAt,
    proposedBy: proposal.proposedBy,
    reason: proposal.reason,
    before,
    after,
  };
  const rows = new Map(state.rows);
  rows.set(proposal.failureModeId, after);
  const approvals = new Map(state.approvals);
  approvals.set(proposal.caseId, approval);
  const proposals = new Map(state.proposals);
  proposals.delete(proposal.caseId);
  return { rows, approvals, proposals };
}

/** The case's action approval, whether its FMEA change is still a draft or already live. */
export function fmeaActionApproval(
  state: FmeaLearningSessionState,
  caseId: string,
): { at: string; by: string; reason?: string } | undefined {
  const live = state.approvals.get(caseId);
  if (live) return { at: live.proposedAt, by: live.proposedBy, ...(live.reason ? { reason: live.reason } : {}) };
  const draft = state.proposals.get(caseId);
  return draft ? { at: draft.proposedAt, by: draft.proposedBy, ...(draft.reason ? { reason: draft.reason } : {}) } : undefined;
}
